import { useEffect, useMemo, useState } from 'react'
import { useWorkspaceStore } from '../stores/workspaceStore'
import { useRegisterDrawer } from '../shell/ShellContext'
import {
  adminApi,
  type AdminAccount,
  type AdminAccountDetail,
  type AuditLogEntry,
} from '../services/adminService'
import {
  Avatar,
  Badge,
  Button,
  Card,
  Chip,
  ChipGroup,
  DataTable,
  Drawer,
  useDrawerContent,
  EmptyState,
  Field,
  FieldGrid,
  FilterBar,
  Icon,
  Inline,
  KeyValue,
  LoadingState,
  Meter,
  Modal,
  PageHeader,
  Pagination,
  SearchField,
  Section,
  Stack,
  Stat,
  TabPanel,
  Tabs,
  TileGrid,
  Timeline,
} from '../ds'

type Tab = 'accounts' | 'audit'
type StatusFilter = '' | 'active' | 'trialing' | 'cancelled'
type AuditFilter = 'all' | 'trial' | 'enterprise' | 'access'

const TAB_ITEMS = [
  { value: 'accounts', label: 'Accounts' },
  { value: 'audit', label: 'Audit log' },
]
const ID_BASE = 'admin'

/** D-29: Admin tables page rather than scroll. */
const PAGE_SIZE = 25

type BadgeTone = 'neutral' | 'gold' | 'sage' | 'danger'
const STATUS_TONE: Record<string, BadgeTone> = { active: 'sage', trialing: 'gold', cancelled: 'danger' }

const cap = (v?: string) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : v)

const STATUS_PILLS: { key: StatusFilter; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'trialing', label: 'Trialing' },
  { key: 'cancelled', label: 'Cancelled' },
]

const AUDIT_PILLS: { key: AuditFilter; label: string }[] = [
  { key: 'all', label: 'All actions' },
  { key: 'trial', label: 'Trials' },
  { key: 'enterprise', label: 'Enterprise' },
  { key: 'access', label: 'Access' },
]

function initials(name?: string | null) {
  if (!name) return '?'
  const parts = name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('')
  return (parts || '?').toUpperCase()
}
function fmtDate(s?: string) {
  return s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
}
function daysLeft(s?: string): number | null {
  if (!s) return null
  return Math.ceil((new Date(s).getTime() - Date.now()) / 86400000)
}
function fmtAgo(timestamp: string) {
  const diff = Date.now() - new Date(timestamp).getTime()
  const m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000)
  const d = Math.floor(diff / 86400000)
  if (m < 1) return 'Just now'
  if (m < 60) return `${m}m ago`
  if (h < 24) return `${h}h ago`
  if (d === 1) return 'Yesterday'
  if (d < 7) return `${d} days ago`
  return new Date(timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

// Map a raw audit action to a human label + the category used by the filter pills.
function describeAudit(e: AuditLogEntry): { label: string; category: AuditFilter } {
  const act = e.action || ''
  const a = act.toLowerCase()
  const reason = typeof e.details?.reason === 'string' ? e.details.reason : ''
  const fallback = act.replace(/_/g, ' ') || 'Admin action'
  if (a.includes('trial')) return { label: reason || 'Extended trial', category: 'trial' }
  if (a.includes('enterprise')) return { label: reason || 'Activated enterprise terms', category: 'enterprise' }
  return { label: reason || fallback, category: 'access' }
}

export default function Admin() {
  const { userMembership } = useWorkspaceStore()
  const isSuperAdmin = !!userMembership?.isSuperAdmin

  const [tab, setTab] = useState<Tab>('accounts')
  const [accounts, setAccounts] = useState<AdminAccount[]>([])
  const [audit, setAudit] = useState<AuditLogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('')
  const [auditFilter, setAuditFilter] = useState<AuditFilter>('all')
  const [offset, setOffset] = useState(0)
  // A new search or filter is a new list: back to its first page.
  useEffect(() => setOffset(0), [status, search])

  const [selected, setSelected] = useState<AdminAccountDetail | null>(null)
  // The drawer is always mounted (MAR-381): `open` flips, and the record it showed is held
  // through the slide-out. Tell the shell it is over the canvas — until the slide-out ends —
  // so the Ask dock collapses to its rail in the middle band like every other drawer.
  const { value: shownAccount, onExited: onDrawerExited } = useDrawerContent(selected, !!selected)
  useRegisterDrawer(!!shownAccount)
  const [detailLoading, setDetailLoading] = useState(false)
  const [action, setAction] = useState<null | 'trial' | 'enterprise'>(null)

  const fetchAccounts = async () => {
    setLoading(true)
    try {
      const r = await adminApi.listAccounts({ status: status || undefined, search: search || undefined })
      if (r.success && r.data) setAccounts(r.data.accounts || [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isSuperAdmin) return
    adminApi.getAuditLog({ limit: 50 }).then((r) => r.success && r.data && setAudit(r.data.entries || [])).catch(() => {})
  }, [isSuperAdmin])

  useEffect(() => {
    if (!isSuperAdmin) return
    const t = setTimeout(fetchAccounts, search ? 300 : 0)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, status, search])

  const openAccount = async (id: string) => {
    setDetailLoading(true)
    setSelected({ id } as AdminAccountDetail)
    try {
      const r = await adminApi.getAccount(id)
      if (r.success && r.data) {
        // The endpoint returns a nested shape; flatten to the detail type.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const d = r.data as any
        setSelected({
          id: d.account?.id ?? id,
          name: d.account?.name,
          createdAt: d.account?.createdAt,
          subscription: d.subscription,
          usage: d.usage,
          workspaceList: d.workspaces,
        } as AdminAccountDetail)
      }
    } finally {
      setDetailLoading(false)
    }
  }

  const stats = useMemo(() => ({
    total: accounts.length,
    active: accounts.filter((a) => a.subscription?.status === 'active').length,
    trialing: accounts.filter((a) => a.subscription?.status === 'trialing').length,
    enterprise: accounts.filter((a) => a.subscription?.tier === 'enterprise').length,
  }), [accounts])

  const visibleAudit = useMemo(
    () => audit.filter((e) => auditFilter === 'all' || describeAudit(e).category === auditFilter),
    [audit, auditFilter]
  )

  if (!isSuperAdmin) {
    return (
      <div className="mv-canvas-col">
        <EmptyState title="Super-admin only">
          This is the Marva internal console. You need super-admin access to view it.
        </EmptyState>
      </div>
    )
  }

  const accountColumns = [
    {
      key: 'account',
      label: 'Account',
      render: (a: AdminAccount) => (
        <Inline wrap={false}>
          <Avatar name={initials(a.name)} size="sm" shape="square" label={false} />
          <span>
            <span>{a.name}</span>
            <br />
            <span className="t-small">
              {[
                a.workspaces && `${a.workspaces} workspace${a.workspaces === 1 ? '' : 's'}`,
                a.users && `${a.users} user${a.users === 1 ? '' : 's'}`,
              ]
                .filter(Boolean)
                .join(' · ') || '—'}
            </span>
          </span>
        </Inline>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (a: AdminAccount) =>
        a.subscription?.status ? (
          <Badge tone={STATUS_TONE[a.subscription.status] ?? 'neutral'}>{a.subscription.status}</Badge>
        ) : null,
    },
    {
      key: 'tier',
      label: 'Tier',
      render: (a: AdminAccount) => (a.subscription?.tier ? <Badge>{a.subscription.tier}</Badge> : null),
    },
    // Secondary: read in the card when the table stacks in a narrow column (DataTable `stack`).
    { key: 'created', label: 'Created', secondary: true, render: (a: AdminAccount) => fmtDate(a.createdAt) },
    {
      key: 'trial',
      label: 'Trial',
      secondary: true,
      render: (a: AdminAccount) => {
        const dl =
          a.subscription?.status === 'trialing'
            ? daysLeft(a.subscription.trialExtendedUntil || a.subscription.trialEndsAt)
            : null
        if (dl === null) return '—'
        return <Badge tone={dl <= 3 ? 'danger' : 'neutral'}>{dl > 0 ? `${dl} days left` : 'Expired'}</Badge>
      },
    },
  ]
  const pageRows = accounts.slice(offset, offset + PAGE_SIZE)

  return (
    <div className="mv-canvas-wide">
      <PageHeader
        eyebrow={
          <Badge tone="gold">
            <Icon d="M12 3l8 4v5c0 4.5-3.2 7.6-8 9-4.8-1.4-8-4.5-8-9V7z|M9 12l2 2 4-4" /> Super-admin · Marva internal
          </Badge>
        }
        title="Accounts & platform."
        sub="Manage customer accounts, trials and enterprise terms — and watch the audit trail of every internal action."
      />

      {/* Underline tabs (D-18): tab roles, arrow keys move between them. */}
      <Tabs tabs={TAB_ITEMS} value={tab} onChange={(v) => setTab(v as Tab)} label="Admin sections" idBase={ID_BASE} />

      <TabPanel value="accounts" active={tab} idBase={ID_BASE}>
        <Stack>
          <TileGrid>
            <Card padding="sm"><Stat value={stats.total} label="Total accounts" /></Card>
            <Card padding="sm"><Stat value={stats.active} label="Active" /></Card>
            <Card padding="sm"><Stat value={stats.trialing} label="Trialing" /></Card>
            <Card padding="sm"><Stat value={stats.enterprise} label="Enterprise" tone="gold" /></Card>
          </TileGrid>

          <FilterBar>
            <SearchField
              value={search}
              onChange={setSearch}
              label="Search accounts"
              placeholder="Search by store or domain…"
              busy={loading && !!search}
            />
            <ChipGroup label="Status">
              {STATUS_PILLS.map((p) => (
                <Chip key={p.label} kind="select" selected={status === p.key} onToggle={() => setStatus(p.key)}>
                  {p.label}
                </Chip>
              ))}
            </ChipGroup>
          </FilterBar>

          {loading && accounts.length === 0 ? (
            <LoadingState caption="Loading accounts…" />
          ) : (
            <>
              <DataTable
                caption="Accounts"
                columns={accountColumns}
                rows={pageRows}
                onRowOpen={(a: AdminAccount) => openAccount(a.id)}
                selectedKey={selected?.id}
                emptyText="No accounts match."
              />
              {accounts.length > PAGE_SIZE && (
                <Pagination offset={offset} limit={PAGE_SIZE} total={accounts.length} onPageChange={setOffset} label="Accounts pages" />
              )}
            </>
          )}
        </Stack>
      </TabPanel>

      <TabPanel value="audit" active={tab} idBase={ID_BASE}>
        <Stack>
          <FilterBar>
            <ChipGroup label="Action">
              {AUDIT_PILLS.map((p) => (
                <Chip key={p.key} kind="select" selected={auditFilter === p.key} onToggle={() => setAuditFilter(p.key)}>
                  {p.label}
                </Chip>
              ))}
            </ChipGroup>
          </FilterBar>
          {visibleAudit.length === 0 ? (
            <EmptyState size="sm">No audit entries.</EmptyState>
          ) : (
            <Card>
              <Timeline
                label="Audit log"
                items={visibleAudit.map((e) => ({
                  when: fmtAgo(e.timestamp),
                  datetime: e.timestamp,
                  title: describeAudit(e).label,
                  detail: e.adminEmail,
                }))}
              />
            </Card>
          )}
        </Stack>
      </TabPanel>

      {/* Mounted for the super-admin whether or not an account is open (the gate above returns
          before it otherwise); `open` is the only thing that changes. */}
      <AccountDrawer
          open={!!selected}
          detail={shownAccount ?? null}
          loading={detailLoading}
          onExited={onDrawerExited}
          onClose={() => { setSelected(null); setAction(null) }}
          onAction={(a) => setAction(a)}
          onImpersonate={async () => {
            if (!selected) return
            const reason = window.prompt('Reason for impersonation:')
            if (!reason) return
            const r = await adminApi.impersonate(selected.id, { reason })
            if (r.success && r.data) {
              // Deliberately do NOT persist the impersonation bearer token in localStorage:
              // it is XSS-exfiltratable, was never cleared, and nothing reads it (latent-bug
              // audit, web-app P5). A real impersonation flow should exchange it for a session
              // server-side rather than stashing the raw token in the browser.
              window.location.reload()
            }
          }}
        />

      {selected && action && (
        <ActionModal
          kind={action}
          accountName={selected.name || 'this account'}
          onClose={() => setAction(null)}
          onSubmit={async (payload) => {
            if (action === 'trial') {
              await adminApi.extendTrial(selected.id, payload as { days: number; reason: string })
            } else {
              const p = payload as { price: number; reason: string; customLimits: Record<string, number> }
              await adminApi.activateEnterprise(selected.id, {
                price: p.price,
                activationReason: p.reason,
                customLimits: p.customLimits,
              })
            }
            setAction(null)
            await openAccount(selected.id)
            await fetchAccounts()
          }}
        />
      )}
    </div>
  )
}

const USAGE_ROWS: { key: 'profiles' | 'conversations' | 'messages'; label: string }[] = [
  { key: 'profiles', label: 'Active customers' },
  { key: 'conversations', label: 'Conversations' },
  { key: 'messages', label: 'Emails' },
]

function AccountDrawer({
  open,
  detail,
  loading,
  onClose,
  onExited,
  onAction,
  onImpersonate,
}: {
  open: boolean
  detail: AdminAccountDetail | null
  loading: boolean
  onClose: () => void
  onExited: () => void
  onAction: (a: 'trial' | 'enterprise') => void
  onImpersonate: () => void
}) {
  // Escape, the scrim and focus are the Drawer's own (useOverlay); so is holding what it showed
  // through the close. With no account (closed, slide-out done) it renders an empty panel.
  const sub = detail?.subscription
  const workspaces = detail?.workspaceList || []
  return (
    <Drawer
      open={open}
      modal={false}
      onClose={onClose}
      onExited={onExited}
      size="sm"
      title={detail ? detail.name || 'Account' : ''}
      subtitle={detail ? workspaces[0]?.shopifyDomain || detail.id : undefined}
      media={detail ? <Avatar name={detail.name ? initials(detail.name) : '—'} size="lg" shape="square" label={false} /> : undefined}
    >
      {!detail ? null : loading && !sub ? (
        <LoadingState caption="Loading…" />
      ) : (
        <Stack gap="lg">
          <KeyValue
            columns={2}
            items={[
              { label: 'Status', value: cap(sub?.status) },
              { label: 'Tier', value: cap(sub?.tier) },
              { label: 'Created', value: fmtDate(detail.createdAt) },
              { label: 'Workspaces', value: String(workspaces.length || detail.workspaces || 0) },
            ]}
          />

          {workspaces.length > 0 && (
            <Section title="Workspaces">
              <KeyValue
                items={workspaces.map((w) => ({
                  label: w.name,
                  value: `${w.shopifyDomain || '—'}${w.mode ? ` · ${w.mode}` : ''}`,
                }))}
              />
            </Section>
          )}

          {detail.usage && (
            <Section title="Usage this month">
              <Stack gap="sm">
                {USAGE_ROWS.map(({ key, label }) => {
                  const m = detail.usage?.[key]
                  if (!m) return null
                  const used = m.used ?? 0
                  const limit = m.limit ?? 0
                  const pct = Math.min(100, Math.round(m.percentage ?? (limit ? (used / limit) * 100 : 0)))
                  return (
                    <Meter
                      key={key}
                      label={label}
                      value={pct}
                      valueText={`${used.toLocaleString()} / ${limit.toLocaleString()}`}
                    />
                  )
                })}
              </Stack>
            </Section>
          )}

          <Section title="Actions">
            <Inline>
              <Button variant="ghost" onClick={() => onAction('trial')}>Extend trial</Button>
              <Button variant="ghost" onClick={() => onAction('enterprise')}>Set enterprise</Button>
              <Button variant="ghost" onClick={onImpersonate}>Impersonate</Button>
            </Inline>
          </Section>
        </Stack>
      )}
    </Drawer>
  )
}

function ActionModal({
  kind,
  accountName,
  onClose,
  onSubmit,
}: {
  kind: 'trial' | 'enterprise'
  accountName: string
  onClose: () => void
  onSubmit: (payload: unknown) => Promise<void>
}) {
  const [days, setDays] = useState(14)
  const [price, setPrice] = useState(0)
  const [reason, setReason] = useState('')
  const [limits, setLimits] = useState({ mau: 100000, conversations: 50000, emails: 500000 })
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!reason.trim()) return
    setBusy(true)
    try {
      if (kind === 'trial') await onSubmit({ days, reason: reason.trim() })
      else await onSubmit({ price, reason: reason.trim(), customLimits: limits })
    } finally {
      setBusy(false)
    }
  }

  const num = (e: React.ChangeEvent<HTMLElement>) => Number((e.target as HTMLInputElement).value)

  return (
    <Modal
      open
      onClose={busy ? undefined : onClose}
      dismissible={!busy}
      title={kind === 'trial' ? 'Extend trial' : 'Set enterprise terms'}
      description={`For ${accountName}.`}
      actions={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" disabled={!reason.trim()} busy={busy} busyLabel="Working…" onClick={submit}>
            {kind === 'trial' ? 'Extend trial' : 'Activate'}
          </Button>
        </>
      }
    >
      <Stack>
        {kind === 'trial' ? (
          <Field label="Extend by (days)" type="number" min={1} value={days} suffix="days" onChange={(e) => setDays(num(e))} />
        ) : (
          <>
            <Field
              label="Monthly price (£) — 0 to comp"
              type="number"
              min={0}
              value={price}
              prefix="£"
              suffix="/ month"
              onChange={(e) => setPrice(num(e))}
            />
            <FieldGrid>
              <Field
                label="Conversations"
                type="number"
                value={limits.conversations}
                suffix="/ month"
                onChange={(e) => setLimits({ ...limits, conversations: num(e) })}
              />
              <Field
                label="Active customers (MAU)"
                type="number"
                value={limits.mau}
                suffix="MAU"
                onChange={(e) => setLimits({ ...limits, mau: num(e) })}
              />
            </FieldGrid>
            <Field
              label="Emails / month"
              type="number"
              value={limits.emails}
              suffix="/ month"
              onChange={(e) => setLimits({ ...limits, emails: num(e) })}
            />
          </>
        )}
        <Field
          label="Reason (logged to the audit trail)"
          value={reason}
          onChange={(e) => setReason((e.target as HTMLInputElement).value)}
          placeholder="Why are you doing this?"
        />
      </Stack>
    </Modal>
  )
}
